import { __decorate } from "tslib";
import { Events, } from '@ag-grid-community/core';
import { css, html, observable } from '@microsoft/fast-element';
import { FoundationElement } from '@microsoft/fast-foundation';
import { EDITED_COLUMN_FIELD } from '../grid-pro.types';
/**
 * The AG Boolean Renderer element.
 * @public
 * @tagname %%prefix%%-boolean-renderer
 */
export class BooleanRenderer extends FoundationElement {
    init(params) {
        if (!params)
            return;
        this.params = params;
        this.selected = params.value;
    }
    changeHandler(e) {
        this.selected = e.target.checked;
        const editedCells = this.params.api.getEditingCells();
        const changingCurrentlyEditedCell = !!editedCells.find((cell) => cell.rowIndex === this.params.rowIndex &&
            cell.column.getInstanceId() === this.params.column.getInstanceId());
        if (!changingCurrentlyEditedCell) {
            // below section allows grid renderer to act as grid editor so users can edit data without requesting edit
            // heavily inspired by https://github.com/ag-grid/ag-grid/blob/latest/grid-community-modules/core/src/ts/rendering/cellRenderers/checkboxCellRenderer.ts#L104
            const { column, node, rowIndex, value } = this.params;
            const eventStarted = {
                type: Events.EVENT_CELL_EDITING_STARTED,
                column: column,
                colDef: column === null || column === void 0 ? void 0 : column.getColDef(),
                data: node.data,
                node,
                rowIndex,
                rowPinned: node.rowPinned,
                value,
            };
            this.params.api.dispatchEvent(eventStarted);
            const valueChanged = this.params.node.setDataValue(this.params.column, this.selected, 'edit');
            const eventStopped = {
                type: Events.EVENT_CELL_EDITING_STOPPED,
                column: column,
                colDef: column === null || column === void 0 ? void 0 : column.getColDef(),
                data: node.data,
                node,
                rowIndex,
                rowPinned: node.rowPinned,
                value,
                oldValue: value,
                newValue: this.selected,
                valueChanged,
            };
            this.params.api.dispatchEvent(eventStopped);
        }
    }
    getGui() {
        return this;
    }
    destroy() { }
    refresh(params) {
        this.selected = params.value;
        return true;
    }
    getValue() {
        return this.selected;
    }
    isCancelBeforeStart() {
        return !!this.params.data[EDITED_COLUMN_FIELD];
    }
}
__decorate([
    observable
], BooleanRenderer.prototype, "params", void 0);
__decorate([
    observable
], BooleanRenderer.prototype, "selected", void 0);
/**
 * The AG Boolean Renderer Styles.
 * @public
 */
export const agBooleanRendererStyles = css `
  :host {
    align-center: center;
    display: flex;
    height: 100%;
  }
`;
/**
 * Get a Design System prefixed Checkbox template.
 * @param designSystem - The design system prefix to use. Defaults to 'foundation'.
 * @returns A Checkbox component template prefixed with the correct design system.
 * @public
 */
export const getAgBooleanRendererTemplate = (designSystem = 'foundation') => {
    const checkboxComponent = `${designSystem}-checkbox`;
    return html `
    <${checkboxComponent}
      @change=${(x, c) => x.changeHandler(c.event)}
      :checked=${(x) => x.selected}
      ?disabled=${(x) => !x.params.colDef.editable}
    ></${checkboxComponent}>
  `;
};
/**
 * A function that returns a Foundation Boolean Renderer registration for configuring the component with a DesignSystem.
 *
 * @public
 * @remarks
 * HTML Element: \<foundation-boolean-renderer\>
 */
export const foundationAgBooleanRenderer = BooleanRenderer.compose({
    baseName: 'boolean-renderer',
    styles: agBooleanRendererStyles,
    template: getAgBooleanRendererTemplate(),
});
